import React, { useState, useEffect } from 'react';
import { FileText, Wine } from 'lucide-react';
import { QtyControl } from './ProductCard';
import { formatProductDisplay } from '../../utils/formatProduct';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

function wineTypeBgClass(type) {
  if (!type) return 'sf-bg--default';
  return `sf-bg--${type
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')}`;
}

// Three gray placeholder bars shown while full product data loads.
// Image and name are already visible from the list data so a spinner
// here would be redundant and jarring.
function ContentSkeleton() {
  return (
    <div className="pb-2">
      {[100, 75, 50].map((w, i) => (
        <div
          key={i}
          style={{
            height: 14,
            background: '#e8e4d4',
            borderRadius: 4,
            marginBottom: 8,
            width: `${w}%`,
          }}
        />
      ))}
    </div>
  );
}

// ── ProductDrawer ─────────────────────────────────────────────────────────────
// Props:
//   product      — product object from list (partial) OR from get (full, has .tiers).
//                  Pass null to unmount.
//   loading      — true while wholesaleProducts.get() is in-flight
//   onClose      — () => void
//   onAddToCart  — (productId, qty) => void

export default function ProductDrawer({ product, loading, onClose, onAddToCart }) {
  // Unmount cleanly when no product is selected
  if (!product) return null;

  return <DrawerInner
    key={product.productID}
    product={product}
    loading={loading}
    onClose={onClose}
    onAddToCart={onAddToCart}
  />;
}

// Separate inner component so that key={productID} resets qty state when
// a different product is opened (while keeping smooth transitions for the
// partial → full data update on the same product).
function DrawerInner({ product, loading, onClose, onAddToCart }) {
  const minQty = product.wholesaleMinQty || 1;
  const [qty, setQty]   = useState(minQty);
  const [added, setAdded] = useState(false);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleAddToCart = () => {
    if (onAddToCart) onAddToCart(product.productID, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const price    = product.displayPrice ?? product.wholesalePrice ?? 0;
  const labelSrc = imgSrc(product.proLabelImageUrl);
  const outOfStock = product.inventory === 0;

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        className="offcanvas-backdrop fade show"
        onClick={onClose}
        style={{ cursor: 'pointer' }}
      />

      {/* ── Offcanvas panel ── */}
      <div
        className="offcanvas offcanvas-end sf-drawer show"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-label={product.proFanciful}
        style={{ width: 420, maxWidth: '100vw' }}
      >
        {/* Header */}
        <div className="offcanvas-header">
          <h5 className="offcanvas-title text-truncate me-3">{product.proBrand || product.proFanciful}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* Body — scrolls independently */}
        <div className="offcanvas-body">

          {/* ── Image section ── */}
          <div className={`sf-drawer__img-section ${wineTypeBgClass(product.proWineType)}`}>
            {labelSrc ? (
              <img
                className="sf-drawer__img"
                src={labelSrc}
                alt={product.proFanciful}
              />
            ) : (
              <Wine size={64} style={{ opacity: 0.2 }} />
            )}
          </div>

          {/* ── Content section ── */}
          <div className="sf-drawer__content">

            {(() => {
              const { primary, secondary } = formatProductDisplay(product);
              return (
                <>
                  <h2 className="sf-drawer__name">{primary}</h2>
                  {secondary && <p className="sf-drawer__meta">{secondary}</p>}
                </>
              );
            })()}

            {/* ── Price + tiers (always show price from list data; tiers come from full data) ── */}
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <span style={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1 }}>
                  ${Number(price).toFixed(2)}
                </span>
                <span className="text-muted small">/case</span>
                {product.hasOverride ? (
                  <span
                    style={{
                      background: 'var(--sf-black)',
                      color: 'var(--sf-cream)',
                      fontSize: '0.72rem',
                      padding: '2px 8px',
                      borderRadius: 3,
                      fontWeight: 600,
                    }}
                  >
                    Your price
                  </span>
                ) : null}
              </div>

              {/* Volume tiers — only present after full data loads */}
              {!loading && product.tiers && product.tiers.length > 0 && (
                <table
                  className="table table-sm mb-0 mt-2"
                  style={{ fontSize: '0.82rem' }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Min cases</th>
                      <th>Price / case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.tiers.map(t => (
                      <tr key={t.tierID}>
                        <td>{t.minQty}+</td>
                        <td>${parseFloat(t.pricePerCase).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Add-to-cart ── */}
            <div className="d-flex align-items-center gap-2 mt-3">
              <QtyControl qty={qty} min={minQty} onChange={setQty} />
              <button
                className="sf-add-btn flex-grow-1 py-2"
                onClick={handleAddToCart}
                disabled={outOfStock}
              >
                {added ? 'Added!' : outOfStock ? 'Out of Stock' : 'Add to Cart'}
              </button>
            </div>
            {minQty > 1 && (
              <p className="text-muted small mt-1 mb-0">
                Minimum order: {minQty} case{minQty !== 1 ? 's' : ''}
              </p>
            )}

            <hr style={{ borderColor: 'var(--sf-border)', margin: '1.25rem 0' }} />

            {/* ── Full content (skeleton while loading) ── */}
            {loading ? (
              <ContentSkeleton />
            ) : (
              <>
                {product.proTastingNotes && (
                  <div className="mb-3">
                    <p className="sf-drawer__section-label">Tasting Notes</p>
                    <p className="small mb-0" style={{ lineHeight: 1.6 }}>
                      {product.proTastingNotes}
                    </p>
                  </div>
                )}

                {product.proDescription && (
                  <div className="mb-3">
                    <p className="sf-drawer__section-label">About This Wine</p>
                    <p className="small mb-0" style={{ lineHeight: 1.6 }}>
                      {product.proDescription}
                    </p>
                  </div>
                )}

                {product.proWinemaker && (
                  <div className="mb-3">
                    <p className="sf-drawer__section-label">Winemaker</p>
                    <p className="small mb-0">{product.proWinemaker}</p>
                  </div>
                )}

                {product.proTechSheetUrl && (
                  <a
                    href={product.proTechSheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-flex align-items-center gap-1 small mb-3"
                  >
                    <FileText size={14} /> Download Tech Sheet
                  </a>
                )}

                <div className="mt-2 small">
                  {product.inventory > 0 ? (
                    <span className="text-muted">{product.inventory} cases available</span>
                  ) : (
                    <span className="text-danger">Currently out of stock</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
